import { useState, useRef, useCallback, useEffect } from 'react';

interface ImageCropModalProps {
  imageFile: File;
  onCrop: (croppedImageBase64: string) => void;
  onCancel: () => void;
}

type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se' | null;

export function ImageCropModal({ imageFile, onCrop, onCancel }: ImageCropModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [resizeCorner, setResizeCorner] = useState<ResizeCorner>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Load image
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Initialize crop area when image loads
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    setImageSize({ width: displayWidth, height: displayHeight });

    // Set initial crop area to be centered and as large as possible
    const initialSize = Math.min(displayWidth, displayHeight) * 0.8;
    setCropArea({
      x: (displayWidth - initialSize) / 2,
      y: (displayHeight - initialSize) / 2,
      size: initialSize,
    });
  }, []);

  // Handle mouse down on crop area
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, action: 'drag' | ResizeCorner) => {
      e.preventDefault();
      e.stopPropagation();

      if (action === 'drag') {
        setIsDragging(true);
      } else {
        setResizeCorner(action);
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    },
    []
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging && !resizeCorner) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      if (isDragging) {
        setCropArea((prev) => {
          const newX = Math.max(0, Math.min(imageSize.width - prev.size, prev.x + deltaX));
          const newY = Math.max(0, Math.min(imageSize.height - prev.size, prev.y + deltaY));
          return { ...prev, x: newX, y: newY };
        });
      } else if (resizeCorner) {
        setCropArea((prev) => {
          let newX = prev.x;
          let newY = prev.y;
          let newSize = prev.size;

          // Calculate delta based on corner being dragged
          switch (resizeCorner) {
            case 'se': {
              // Bottom-right: expand down-right
              const avgDelta = (deltaX + deltaY) / 2;
              newSize = prev.size + avgDelta;
              break;
            }
            case 'sw': {
              // Bottom-left: expand down-left (x moves, size changes)
              const avgDelta = (-deltaX + deltaY) / 2;
              newSize = prev.size + avgDelta;
              newX = prev.x - avgDelta;
              break;
            }
            case 'ne': {
              // Top-right: expand up-right (y moves, size changes)
              const avgDelta = (deltaX - deltaY) / 2;
              newSize = prev.size + avgDelta;
              newY = prev.y - avgDelta;
              break;
            }
            case 'nw': {
              // Top-left: expand up-left (x and y move, size changes)
              const avgDelta = (-deltaX - deltaY) / 2;
              newSize = prev.size + avgDelta;
              newX = prev.x - avgDelta;
              newY = prev.y - avgDelta;
              break;
            }
          }

          // Clamp size
          const minSize = 50;
          newSize = Math.max(minSize, newSize);

          // Clamp position and size to stay within bounds
          if (newX < 0) {
            newSize = newSize + newX;
            newX = 0;
          }
          if (newY < 0) {
            newSize = newSize + newY;
            newY = 0;
          }
          if (newX + newSize > imageSize.width) {
            newSize = imageSize.width - newX;
          }
          if (newY + newSize > imageSize.height) {
            newSize = imageSize.height - newY;
          }

          // Final size clamp
          newSize = Math.max(minSize, newSize);

          return { x: newX, y: newY, size: newSize };
        });
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    },
    [isDragging, resizeCorner, dragStart, imageSize]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setResizeCorner(null);
  }, []);

  // Handle crop confirm
  const handleCrop = useCallback(() => {
    const img = imageRef.current;
    if (!img || !imageSrc) return;

    // Calculate the scale between displayed and natural image size
    const scaleX = img.naturalWidth / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight;

    // Create canvas for cropping
    const canvas = document.createElement('canvas');
    const outputSize = 256; // Output square size
    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate source coordinates in natural image size
    const srcX = cropArea.x * scaleX;
    const srcY = cropArea.y * scaleY;
    const srcSize = cropArea.size * Math.min(scaleX, scaleY);

    // Draw cropped and scaled image
    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, outputSize, outputSize);

    // Get base64
    const croppedBase64 = canvas.toDataURL('image/jpeg', 0.9);
    onCrop(croppedBase64);
  }, [imageSrc, cropArea, onCrop]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  if (!imageSrc) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent" />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-wall-surface border border-wall-border/50 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-wall-border/30">
          <h2 className="text-xl font-bold text-gradient">Crop Profile Picture</h2>
          <p className="text-sm text-wall-muted mt-1">Drag to move, drag corner to resize</p>
        </div>

        {/* Crop Area */}
        <div
          ref={containerRef}
          className="relative p-6 flex items-center justify-center bg-wall-bg/50"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="relative inline-block max-w-full max-h-[50vh]">
            {/* Image */}
            <img
              ref={imageRef}
              src={imageSrc}
              alt="Crop preview"
              className="max-w-full max-h-[50vh] object-contain select-none"
              onLoad={handleImageLoad}
              draggable={false}
            />

            {/* Darkened overlay outside crop area */}
            {imageSize.width > 0 && (
              <>
                {/* Top */}
                <div
                  className="absolute left-0 top-0 right-0 bg-black/60 pointer-events-none"
                  style={{ height: cropArea.y }}
                />
                {/* Bottom */}
                <div
                  className="absolute left-0 right-0 bottom-0 bg-black/60 pointer-events-none"
                  style={{ height: imageSize.height - cropArea.y - cropArea.size }}
                />
                {/* Left */}
                <div
                  className="absolute left-0 bg-black/60 pointer-events-none"
                  style={{
                    top: cropArea.y,
                    width: cropArea.x,
                    height: cropArea.size,
                  }}
                />
                {/* Right */}
                <div
                  className="absolute right-0 bg-black/60 pointer-events-none"
                  style={{
                    top: cropArea.y,
                    width: imageSize.width - cropArea.x - cropArea.size,
                    height: cropArea.size,
                  }}
                />

                {/* Crop selection box */}
                <div
                  className="absolute border-2 border-white cursor-move"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.size,
                    height: cropArea.size,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'drag')}
                >
                  {/* Grid lines */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
                    <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
                    <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
                  </div>

                  {/* Corner handles */}
                  <div
                    className="absolute -left-1.5 -top-1.5 w-4 h-4 bg-white border-2 border-wall-accent rounded-xs cursor-nw-resize hover:bg-wall-accent hover:border-white transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'nw')}
                  />
                  <div
                    className="absolute -right-1.5 -top-1.5 w-4 h-4 bg-white border-2 border-wall-accent rounded-xs cursor-ne-resize hover:bg-wall-accent hover:border-white transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'ne')}
                  />
                  <div
                    className="absolute -left-1.5 -bottom-1.5 w-4 h-4 bg-white border-2 border-wall-accent rounded-xs cursor-sw-resize hover:bg-wall-accent hover:border-white transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'sw')}
                  />
                  <div
                    className="absolute -right-1.5 -bottom-1.5 w-4 h-4 bg-white border-2 border-wall-accent rounded-xs cursor-se-resize hover:bg-wall-accent hover:border-white transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'se')}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="px-6 py-4 border-t border-wall-border/30 flex items-center gap-4">
          <div className="text-sm text-wall-muted">Preview:</div>
          <div className="flex gap-3">
            {imageSrc && imageSize.width > 0 && (
              <div className="flex items-center gap-3">
                {/* Large preview */}
                <div
                  className="w-16 h-16 rounded-full overflow-hidden border-2 border-wall-border"
                  style={{
                    backgroundImage: `url(${imageSrc})`,
                    backgroundSize: `${(imageSize.width / cropArea.size) * 64}px ${(imageSize.height / cropArea.size) * 64}px`,
                    backgroundPosition: `${-(cropArea.x / cropArea.size) * 64}px ${-(cropArea.y / cropArea.size) * 64}px`,
                  }}
                />
                {/* Small preview */}
                <div
                  className="w-9 h-9 rounded-full overflow-hidden border-2 border-wall-border"
                  style={{
                    backgroundImage: `url(${imageSrc})`,
                    backgroundSize: `${(imageSize.width / cropArea.size) * 36}px ${(imageSize.height / cropArea.size) * 36}px`,
                    backgroundPosition: `${-(cropArea.x / cropArea.size) * 36}px ${-(cropArea.y / cropArea.size) * 36}px`,
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-wall-border/30 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium bg-wall-border/50 hover:bg-wall-border border border-wall-border rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className="px-4 py-2 text-sm font-medium bg-linear-to-r from-wall-accent to-wall-accent-secondary rounded-lg hover:opacity-90 transition-opacity"
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}
